import { Inject, Injectable } from '@angular/core';
import { HttpInterceptorService } from '../../core/services/http-interceptor.service';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import { Store } from '@ngrx/store';
import { IgenomicsState } from '../../core/interfaces/genomics-state.interface';
import { Observable } from 'rxjs/Observable';
import { ResourceRestService } from "@ispm/workflow/services/resource-rest.service";
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';

export class Track {
  name: string;
  fileId: string;
  fileUrl: string;
  indexUrl: string;
  type: string;
  version: string;
}

@Injectable()
export class GenomeViewerService {
  public static baseUrlGenomeViewer = '/genomeViewer';
  public static baseUrlAberrationService = '/aberration';
  public static baseUrlFileSource = '/fileresource' ;
  public static endPointGenerateFileUrl = '/generateFileUrl';
  private options: {
    headers?: HttpHeaders | {
      [header: string]: string | string[];
    };
    observe: 'response';
    params?: HttpParams | {
      [param: string]: string | string[];
    };
    reportProgress?: boolean;
    responseType?: 'json';
    withCredentials?: boolean;
  } = { observe: 'response'};

  constructor(private _http: HttpInterceptorService, private store: Store<IgenomicsState>,
              private http: HttpClient,
              private resourceRest: ResourceRestService,
              private authService: AuthenticationService,
              private notificationMessageService: NotificationMessageService) {
  }

  public getReferenceTrack(ref: any) {
    if (ref != null) {
      if (ref.type != 'external') {
        return this._http.get('/reference?groupDisplay=' + ref.groupDisplay + '&fasta=' + ref.fastaURL + '&cytoband=' + ref.cytobandURL)
          .map(data => data.json());
      } else {
        return Observable.of(ref);
      }
    }
  }

  public getConstTracks() {

  }

  public getSpecificScanTrack(revisionId): Observable<any> {
    return this._http.get('/track?context=' + revisionId)
      .map(data => data.json());
  }

  public setAberrations(aberrations) {
    const body: any = { };
    body.aberrations = aberrations;
    this._http.post(GenomeViewerService.baseUrlGenomeViewer + '/setAberrations', body).subscribe();
  }

  public getJson() {
    return this._http.get(GenomeViewerService.baseUrlGenomeViewer + '/getJson')
      .map(data => data.json());
  }

  public getAberrationsWithDataById(ids: string[], revisionId: string, testTypeId: string, aberrationType: string) {
    const body: any = { };
    body.ids = ids;
    body.revisionId = revisionId;
    body.testTypeId = testTypeId;
    body.aberrationType = aberrationType;
    return this.resourceRest.post('/aberration/getAberrationsById', body).map(data => data.json());
  }

  public getNextAberration(revisionId: string, testTypeId: string, modality: string, aberrationType: string, filterQueryString: string, selectedIds: string,
                           currentAberrationId: string, currentSort: { dir: string, column: string, sortAsExpression: string }) {
    const body: any = { };
    body.revision_id = revisionId;
    body.test_type_id = testTypeId;
    body.aberrationType = aberrationType;
    body.current_modality = modality;
    body.filter_query = filterQueryString;
    body.selected_ids = selectedIds;
    body.current_id = currentAberrationId;
    body.currentSort = currentSort;
    return this.resourceRest.post(GenomeViewerService.baseUrlGenomeViewer + '/getNext', body).map(data => data.json());
  }

  public getPreviousAberration(revisionId: string, testTypeId: string, modality: string, aberrationType: string, filterQueryString: string, selectedIds: string,
                               currentAberrationId: string, currentSort: { dir: string, column: string, sortAsExpression: string }) {
    const body: any = { };
    body.revision_id = revisionId;
    body.test_type_id = testTypeId;
    body.aberrationType = aberrationType;
    body.current_modality = modality;
    body.filter_query = filterQueryString;
    body.selected_ids = selectedIds;
    body.current_id = currentAberrationId;
    body.currentSort = currentSort;
    return this.resourceRest.post(GenomeViewerService.baseUrlGenomeViewer + '/getPrevious', body).map(data => data.json());
  }

  public getAberrationById(revisionId: string, testTypeId: string, aberrationType: string, ids: string[]) {
    const body: any = { };
    body.ids = ids;
    body.revisionId = revisionId;
    body.testTypeId = testTypeId;
    body.aberrationType = aberrationType;
    return this.resourceRest.post(GenomeViewerService.baseUrlAberrationService + '/getAberrationsById', body).map(data => data.json());
  }

  public getAberrations(revisionId: string, testTypeId: string, aberrationType: string, filterQueryString: string, selectedIds: string) {
    this.options.headers = this.buildHeaders(revisionId, testTypeId, aberrationType, filterQueryString, selectedIds);
    return this.resourceRest.get(GenomeViewerService.baseUrlGenomeViewer + '/getJson?skip_ids=false', this.options)
      .map(response => {
        return response.body;
      })
      .catch((err) => this.responseErrorHandler(err));

  }

  private buildHeaders(revisionId: string, testTypeId: string, aberrationType: string, filterQueryString: string, selectedIds: string): HttpHeaders {
    let headers = new HttpHeaders();

    const currentUser = this.authService.user,
      // access_token = currentUser ? currentUser.access_token : null,
      external_id = currentUser ? currentUser.hsdpId : null,
      xsrf = currentUser ? currentUser.xsrf : null,
      organization = currentUser ? currentUser.organization : null;

    headers = headers.append('Cache-Control', 'no-cache');
    headers = headers.append('timezone_offset', ((new Date()).getTimezoneOffset()).toString());
    if (organization) {
      // headers = headers.append('access_token', access_token);
      headers = headers.append('xsrf', xsrf);
      headers = headers.append('external_id', external_id);
      headers = headers.append('auth_org_id', organization);
    } else if (xsrf) {
      headers = headers.append('xsrf', xsrf);
    }
    headers = headers.append('revision_id', revisionId);
    headers = headers.append('test_type_id', testTypeId);
    headers = headers.append('current_modality', aberrationType);
    headers = headers.append('filter_query', filterQueryString);
    headers = headers.append('selected_ids', selectedIds);
    headers = headers.append('add_range', 'false');
    return headers;

  }

  private responseErrorHandler(err) {
    switch (err.status) {
      case 401:
        this.authService.logout({ refresh: true});
        break;
      case 403:
        this.notificationMessageService.error('You are not allowed to make this action!');
        break;
    }
    return Observable.throw(err);
  }

}
